import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Globe, Wand2, Type, Settings as SettingsIcon, Image as ImageIcon, Film, LayoutList, Save, Loader2, CheckCircle, RefreshCw, PenTool, X, Search, ArrowRight, LogOut, User, Shield, Key } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { autoTranslateObject, translateSRT, autoTagStory, autoRewriteSynopsis } from '../services/ai';
import JoditEditor from 'jodit-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ListManagerModal from '../components/vod/ListManagerModal';
import DriveUploader from '../components/vod/DriveUploader';

const ARCHIVE_USER_EMAIL = 'YOUR_EMAIL@DOMAIN.COM';

const updateUrlParam = (url: string, param: string, value: string) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    if (value) {
      u.searchParams.set(param, value);
    } else {
      u.searchParams.delete(param);
    }
    return u.toString();
  } catch (e) {
    return url;
  }
};

const getUrlParam = (url: string, param: string) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    return u.searchParams.get(param) || '';
  } catch (e) {
    return '';
  }
};

const LANGUAGES = [
  { code: 'ar', label: 'العربية' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'heb', label: 'עברית' },
];

const COUNTRIES_LIST = [
  { value: "Morocco", label: { en: "Morocco", ar: "المغرب" } },
  { value: "Egypt", label: { en: "Egypt", ar: "مصر" } },
  { value: "USA", label: { en: "USA", ar: "الولايات المتحدة" } },
  { value: "UK", label: { en: "UK", ar: "المملكة المتحدة" } },
  { value: "France", label: { en: "France", ar: "فرنسا" } },
  { value: "Spain", label: { en: "Spain", ar: "إسبانيا" } },
  { value: "Germany", label: { en: "Germany", ar: "ألمانيا" } },
  { value: "Netherlands", label: { en: "Netherlands", ar: "هولندا" } },
  { value: "Saudi Arabia", label: { en: "Saudi Arabia", ar: "السعودية" } },
  { value: "UAE", label: { en: "UAE", ar: "الإمارات" } },
  { value: "Lebanon", label: { en: "Lebanon", ar: "لبنان" } },
  { value: "Syria", label: { en: "Syria", ar: "سوريا" } }
];

const LANGUAGES_LIST = [
  { value: "Arabic", label: { en: "Arabic", ar: "العربية" } },
  { value: "English", label: { en: "English", ar: "الإنجليزية" } },
  { value: "French", label: { en: "French", ar: "الفرنسية" } },
  { value: "Spanish", label: { en: "Spanish", ar: "الإسبانية" } },
  { value: "Dutch", label: { en: "Dutch", ar: "الهولندية" } },
  { value: "German", label: { en: "German", ar: "الألمانية" } },
  { value: "Hebrew", label: { en: "Hebrew", ar: "العبرية" } },
  { value: "Moroccan Darija", label: { en: "Moroccan Darija", ar: "الدارجة المغربية" } },
  { value: "Amazigh", label: { en: "Amazigh", ar: "الأمازيغية" } },
  { value: "Riffian", label: { en: "Riffian", ar: "الريفية" } },
  { value: "Tashelhit", label: { en: "Tashelhit", ar: "تشلحيت" } },
  { value: "Souss", label: { en: "Souss", ar: "السوسية" } },
  { value: "Kabyle", label: { en: "Kabyle", ar: "القبايلية" } }
];

const KNOWN_GENRES: Record<string, { en: string, ar: string }> = {
  "Action": { en: "Action", ar: "أكشن" },
  "أكشن": { en: "Action", ar: "أكشن" },
  "Drama": { en: "Drama", ar: "دراما" },
  "دراما": { en: "Drama", ar: "دراما" },
  "Comedy": { en: "Comedy", ar: "كوميديا" },
  "كوميديا": { en: "Comedy", ar: "كوميديا" },
  "Thriller": { en: "Thriller", ar: "إثارة" },
  "إثارة": { en: "Thriller", ar: "إثارة" },
  "Horror": { en: "Horror", ar: "رعب" },
  "رعب": { en: "Horror", ar: "رعب" },
  "Documentary": { en: "Documentary", ar: "وثائقي" },
  "وثائقي": { en: "Documentary", ar: "وثائقي" },
  "Romance": { en: "Romance", ar: "رومانسية" },
  "رومانسية": { en: "Romance", ar: "رومانسية" },
  "Animation": { en: "Animation", ar: "رسوم متحركة" },
  "رسوم متحركة": { en: "Animation", ar: "رسوم متحركة" },
};

interface Translation {
  title: string;
  synopsis: string;
  image_url?: string;
  video_url?: string;
  audio_url?: string;
  subtitle_url?: string;
  timingfile_url?: string;
  storytxt_url?: string;
}

interface EpisodeTranslation {
  title: string;
  overview: string;
  image_url?: string;
  video_url?: string;
  audio_url?: string;
  subtitle_url?: string;
  timingfile_url?: string;
  storytxt_url?: string;
}

interface Episode {
  cuid?: string;
  season: string;
  episode_number: string;
  translations: Record<string, EpisodeTranslation>;
}

interface DropdownItem {
  id: number;
  name: string;
}

interface ArchiveFolder {
  identifier: string;
  title?: string;
}

interface ArchiveFileItem {
  name: string;
  format?: string;
  size?: number | string;
}

interface ArchiveFileExplorerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  userEmail: string;
}

export function ArchiveFileExplorer({ isOpen, onClose, onSelect, userEmail: initialEmail }: ArchiveFileExplorerProps) {
  const [folders, setFolders] = useState<ArchiveFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<ArchiveFolder | null>(null);
  const [files, setFiles] = useState<ArchiveFileItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [uploaderEmail, setUploaderEmail] = useState<string>(initialEmail || '');
  const [useProxy, setUseProxy] = useState<boolean>(false);

  // CORS proxy for Archive.org API (since Archive.org doesn't allow CORS from all origins)
  const corsProxy = (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;

  useEffect(() => {
    if (isOpen) {
      setSelectedFolder(null);
      setFiles([]);
      setError('');
      setSearchTerm('');
      const emailToFetch = uploaderEmail || initialEmail || '';
      if (emailToFetch && emailToFetch !== 'YOUR_EMAIL@DOMAIN.COM') {
        fetchFolders(emailToFetch);
      }
    }
  }, [isOpen]);

  const fetchWithCors = async (url: string) => {
    try {
      const res = await fetch(useProxy ? corsProxy(url) : url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      // If direct fetch fails and we're not using proxy, try with proxy
      if (!useProxy) {
        setUseProxy(true);
        return await fetchWithCors(url);
      }
      throw e;
    }
  };

  const fetchFolders = async (emailOrQuery?: string) => {
    const q = emailOrQuery || uploaderEmail;
    if (!q || q === 'YOUR_EMAIL@DOMAIN.COM') {
      setError('يرجى كتابة البريد الإلكتروني أو اسم المستخدم المرفوع تحته في Archive.org لطلب البيانات.');
      setFolders([]);
      return;
    }
    setLoading(true);
    setError('');
    setUseProxy(false);
    try {
      const query = `uploader:(${q}) OR identifier:(${q})`;
      const url = `https://archive.org/advancedsearch.php?q=${encodeURIComponent(query)}&fl[]=identifier,title&sort[]=addeddesc&rows=100&output=json`;
      const data = await fetchWithCors(url);
      const docs = data.response?.docs || [];
      setFolders(docs);
      if (docs.length === 0) {
        setError(`لم يتم العثور على أي عناصر مرفوعة تحت: ${q}`);
      }
    } catch (e: any) {
      console.error(e);
      if (e.message.includes('CORS') || e.message.includes('Failed to fetch') || e.message.includes('network')) {
        setError('حظر CORS: لا يمكن الوصول لـ Archive.org API مباشرة من المتصفح. تم تجربة بروكسي CORS. إذا استمرت المشكلة، يرجى نسخ رابط الملف يدوياً من Archive.org.');
      } else {
        setError(e.message || 'حدث خطأ أثناء الاتصال بـ Archive.org');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = async (folder: ArchiveFolder) => {
    setSelectedFolder(folder);
    setLoading(true);
    setError('');
    setSearchTerm('');
    setUseProxy(false);
    try {
      const url = `https://archive.org/metadata/${folder.identifier}`;
      const data = await fetchWithCors(url);
      const rawFiles: ArchiveFileItem[] = data.files || [];
      const filtered = rawFiles.filter(f => {
        if (!f.name) return false;
        const lower = f.name.toLowerCase();
        if (lower.endsWith('_meta.xml') || lower.endsWith('_files.xml') || lower.endsWith('_meta.sqlite') || lower.endsWith('_archive.torrent') || lower.endsWith('_thumb.png')) {
          return false;
        }
        return true;
      });
      setFiles(filtered);
    } catch (e: any) {
      console.error(e);
      if (e.message.includes('CORS') || e.message.includes('Failed to fetch') || e.message.includes('network')) {
        setError('حظر CORS: لا يمكن جلب ملفات المجلد مباشرة. تم تجربة بروكسي CORS.');
      } else {
        setError(e.message || 'حدث خطأ أثناء جلب الملفات');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInsertLink = (fileName: string) => {
    if (!selectedFolder) return;
    const directUrl = `https://archive.org/download/${selectedFolder.identifier}/${encodeURI(fileName)}`;
    onSelect(directUrl);
    onClose();
  };

  if (!isOpen) return null;

  const filteredFolders = folders.filter(f => 
    (f.title || f.identifier).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFiles = files.filter(f => 
    f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm p-4 font-arabic" dir="rtl">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
          <div className="flex items-center gap-3">
            <span className="text-2xl" role="img" aria-label="Archive">🏛️</span>
            <div>
              <h3 className="font-bold text-lg leading-tight">مستكشف ملفات Archive.org</h3>
              <p className="text-xs text-slate-300">
                {selectedFolder ? `المجلد: ${selectedFolder.title || selectedFolder.identifier}` : 'استعراض واختيار ملفات الميديا المرفوعة'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {useProxy && (
              <span className="px-2 py-0.5 bg-amber-600 text-white text-[10px] font-medium rounded">CORS Proxy Active</span>
            )}
            <button
              onClick={onClose}
              type="button"
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="إغلاق"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Account Search Input */}
        <div className="p-3 bg-slate-100 border-b border-slate-200 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-700">بريد الحساب في Archive.org:</span>
          <input
            type="text"
            value={uploaderEmail}
            onChange={(e) => setUploaderEmail(e.target.value)}
            placeholder="أدخل بريدك في Archive.org (مثال: user@domain.com)"
            className="flex-1 min-w-[200px] px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            dir="ltr"
          />
          <button
            type="button"
            onClick={() => { setUseProxy(!useProxy); fetchFolders(); }}
            disabled={loading}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-colors flex items-center gap-1 ${useProxy ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
            title={useProxy ? 'Disable CORS Proxy' : 'Enable CORS Proxy (if Archive.org blocks direct access)'}
          >
            {useProxy ? '🔄 Proxy ON' : '🔗 Direct'}
          </button>
          <button
            type="button"
            onClick={() => fetchFolders()}
            disabled={loading}
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            جلب الحساب
          </button>
        </div>

        {/* Toolbar & Filter */}
        <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3">
          {selectedFolder ? (
            <button
              onClick={() => { setSelectedFolder(null); setSearchTerm(''); }}
              type="button"
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 text-xs font-semibold shadow-sm transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              الرجوع إلى المجلدات
            </button>
          ) : (
            <span className="text-xs font-medium text-slate-600">اختر مجلداً لاستعراض ملفاته:</span>
          )}

          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute right-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="تصفية النتائج..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-9 pl-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 flex-1 overflow-y-auto min-h-[320px]">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-sm font-medium">جاري تحميل البيانات من Archive.org...</p>
            </div>
          )}

          {error && !loading && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm text-center">
              {error}
            </div>
          )}

          {/* Folders View */}
          {!loading && !selectedFolder && (
            <div>
              {filteredFolders.length === 0 && !error ? (
                <div className="text-center py-12 text-slate-500 text-sm">
                  يرجى إدخال البريد الإلكتروني بحسابك على Archive.org والضغط على "جلب الحساب".
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {filteredFolders.map((folder) => (
                    <button
                      key={folder.identifier}
                      onClick={() => handleFolderClick(folder)}
                      type="button"
                      className="p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-500 hover:shadow-md transition-all text-right group flex flex-col justify-between gap-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors text-xl">
                          📁
                        </div>
                        <div className="overflow-hidden">
                          <h4 className="font-semibold text-sm text-slate-800 line-clamp-2 leading-snug group-hover:text-indigo-600 transition-colors">
                            {folder.title || folder.identifier}
                          </h4>
                          <span className="text-[11px] text-slate-400 font-mono mt-1 block truncate" dir="ltr">
                            {folder.identifier}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Files View */}
          {!loading && selectedFolder && (
            <div>
              {filteredFiles.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-sm">
                  لا توجد ملفات في هذا المجلد.
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors gap-3"
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <span className="text-xl flex-shrink-0">
                          {/\.(mp3|m4a|wav|aac|ogg|flac)$/i.test(file.name) ? '🎵' : 
                           /\.(mp4|mkv|avi|mov)$/i.test(file.name) ? '🎬' : 
                           /\.(srt|vtt)$/i.test(file.name) ? '📝' : '📄'}
                        </span>
                        <div className="overflow-hidden">
                          <p className="text-xs font-semibold text-slate-800 truncate" dir="ltr">
                            {file.name}
                          </p>
                          {file.format && (
                            <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded font-mono inline-block mt-0.5">
                              {file.format}
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleInsertLink(file.name)}
                        type="button"
                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-semibold transition-colors flex-shrink-0 flex items-center gap-1 shadow-sm"
                      >
                        أدخل الرابط (Insert Link)
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-slate-100 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            type="button"
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-semibold transition-colors"
          >
            إغلاق
          </button>
        </div>

      </div>
    </div>
  );
}

export default function VodManager() {
  const [loading, setLoading] = useState(false);
  const [isTranslatingSrt, setIsTranslatingSrt] = useState(false);
  const [isRewritingSynopsis, setIsRewritingSynopsis] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeLang, setActiveLang] = useState('ar');
  
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listModalType, setListModalType] = useState<'categories' | 'genres'>('categories');
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  
  // Archive File Explorer Modal States
  const [isArchiveOpen, setIsArchiveOpen] = useState<boolean>(false);
  const [activeEpisodeIndex, setActiveEpisodeIndex] = useState<number | null>(null);
  const [activeField, setActiveField] = useState<string>('');

  // Base State
  const [type, setType] = useState<'Single/Movie' | 'Series' | 'Text Book'>('Single/Movie');
  const [isKidsMode, setIsKidsMode] = useState(false);
  
  // Translations Object for Story
  const [contentTranslations, setContentTranslations] = useState<Record<string, Translation>>({});
  
  const [category, setCategory] = useState('');
  const [director, setDirector] = useState('');
  const [writer, setWriter] = useState('');
  const [audio, setAudio] = useState('');
  const [genres, setGenres] = useState('');
  const [castMembers, setCastMembers] = useState('');
  const [releaseYear, setReleaseYear] = useState('');
  const [duration, setDuration] = useState('');
  const [country, setCountry] = useState('');
  const [originalLanguage, setOriginalLanguage] = useState('');
  const [rating, setRating] = useState('');
  const [tmdbId, setTmdbId] = useState('');
  
  const [posterImage, setPosterImage] = useState('');
  const [verticalImage, setVerticalImage] = useState('');
  const [titleImage, setTitleImage] = useState('');
  const [trailerStreamUrl, setTrailerStreamUrl] = useState('');
  
  // New Fields
  const [author, setAuthor] = useState('');
  const [illustrator, setIllustrator] = useState('');
  const [channelId, setChannelId] = useState('');
  const [channels, setChannels] = useState<any[]>([]);
  const [status, setStatus] = useState('published');

  // Monetization
  const [isPremium, setIsPremium] = useState(false);
  const [previewDuration, setPreviewDuration] = useState('60');
  const [price, setPrice] = useState('0');
  const [difficultyLevel, setDifficultyLevel] = useState('');
  
  // Series State
  const [episodes, setEpisodes] = useState<Episode[]>([]);

  // Categories & Genres State
  const [categoriesList, setCategoriesList] = useState<DropdownItem[]>([]);
  const [genresList, setGenresList] = useState<DropdownItem[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newGenreName, setNewGenreName] = useState('');

  // HTML Editor States
  const [htmlContent, setHtmlContent] = useState<Record<string, string>>({});
  const [isHtmlEditorOpen, setIsHtmlEditorOpen] = useState<Record<string, boolean>>({});
  const [uploadingHtml, setUploadingHtml] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

  const { cuid } = useParams<{ cuid: string }>();
  const navigate = useNavigate();

  const openArchiveExplorer = (index: number | null, field: string) => {
    setActiveEpisodeIndex(index);
    setActiveField(field);
    setIsArchiveOpen(true);
  };

  const handleArchiveSelect = (url: string) => {
    if (activeEpisodeIndex !== null) {
      handleEpisodeTranslationChange(activeEpisodeIndex, activeField as any, url);
    } else {
      if (activeField === 'verticalImage') setVerticalImage(url);
      else if (activeField === 'posterImage') setPosterImage(url);
      else if (activeField === 'titleImage') setTitleImage(url);
      else if (activeField === 'trailerStreamUrl') setTrailerStreamUrl(url);
      else {
        handleContentTranslationChange(activeField as any, url);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleSwitchAccount = () => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('token');
    setShowAccountMenu(false);
    navigate('/login');
  };

  // Click outside to close account menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setShowAccountMenu(false);
      }
    };
    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountMenu]);

  const getSourceLangCode = () => {
    switch (originalLanguage) {
      case 'English': return 'en';
      case 'French': return 'fr';
      case 'Spanish': return 'es';
      case 'Dutch': return 'nl';
      case 'German': return 'de';
      case 'Hebrew': return 'heb';
      default: return 'ar';
    }
  };

  const handleTranslateSubtitle = async () => {
    if (!cuid) return;
    const sourceLangCode = getSourceLangCode();
    let sourceSubtitleUrl = contentTranslations[sourceLangCode]?.subtitle_url;
    if (!sourceSubtitleUrl) {
      const langWithSub = Object.keys(contentTranslations).find(k => contentTranslations[k]?.subtitle_url);
      if (langWithSub) sourceSubtitleUrl = contentTranslations[langWithSub]?.subtitle_url;
    }
    
    if (!sourceSubtitleUrl) {
      setError('Please upload a source subtitle file first before translating.');
      return;
    }

    if (activeLang === sourceLangCode && !contentTranslations[activeLang]?.subtitle_url) {
      setError('You are currently on the source language tab. Please upload the original subtitle here first.');
      return;
    }
    
    setIsTranslatingSrt(true);
    setError('');
    
    try {
      const res = await fetch(sourceSubtitleUrl);
      if (!res.ok) throw new Error('Failed to fetch source subtitle file.');
      const srtText = await res.text();
      
      const langLabel = LANGUAGES.find(l => l.code === activeLang)?.label || activeLang;
      const translatedSrtText = await translateSRT(srtText, langLabel);
      
      if (!translatedSrtText) throw new Error('AI returned empty translation.');

      const token = localStorage.getItem('jwt_token');
      const blob = new Blob([translatedSrtText], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', blob, `subtitle_${activeLang}.srt`);
      
      const uploadRes = await fetch(`${API_URL}/stories/${cuid}/media/subtitle_${activeLang}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(uploadData.error || 'Failed to upload translated subtitle');
      
      const host = API_URL.replace(/\/api\/v1\/?$/, '');
      const absoluteUrl = uploadData.url.startsWith('http') ? uploadData.url : `${host}${uploadData.url.startsWith('/') ? '' : '/'}${uploadData.url}`;
      handleContentTranslationChange('subtitle_url', absoluteUrl);
      setSuccess(`Subtitle successfully translated to ${langLabel}!`);
    } catch (e: any) {
      console.error(e);
      setError(e.message || 'Error translating subtitle.');
    } finally {
      setIsTranslatingSrt(false);
    }
  };

  useEffect(() => {
    fetchLists();

    fetch(`${API_URL}/content/channels`)
      .then(res => res.json())
      .then(data => setChannels(data))
      .catch(err => console.error('Error fetching channels:', err));

    if (cuid) {
      fetchStoryDetails();
    }
  }, [cuid]);

  const fetchStoryDetails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/content/story/${cuid}?all=true`);
      if (res.ok) {
        let dataArr = await res.json();
        if (dataArr && !Array.isArray(dataArr) && dataArr.cuid) {
           dataArr = [dataArr];
        }
        if (dataArr && dataArr.length > 0) {
          const s = dataArr[0];
          
          const determinedType = s.type === 'Text Book' ? 'Text Book' : (s.episodes ? 'Series' : 'Single/Movie');
          setType(determinedType);
          setCategory(s.category || '');
          setDirector(s.metadata?.director || '');
          setWriter(s.metadata?.writer || '');
          setAuthor(s.metadata?.author || s.author || '');
          setIllustrator(s.metadata?.illustrator || s.illustrator || '');
          setChannelId(s.channel_id || '');
          setStatus(s.status || 'published');
          setDifficultyLevel(s.metadata?.difficulty_level || '');
          setAudio(s.metadata?.audio_language || '');
          setGenres(s.metadata?.genres || '');
          setCastMembers(s.metadata?.cast_members || '');
          setReleaseYear(s.metadata?.release_year?.toString() || '');
          setDuration(s.metadata?.duration_minutes || '');
          setCountry(s.metadata?.production_country || '');
          setOriginalLanguage(s.metadata?.original_language || '');
          setRating(s.metadata?.age_rating || '');
          setTmdbId(s.metadata?.external_id || '');
          setVerticalImage(s.images?.poster_vertical_url || '');
          setPosterImage(s.images?.poster_horizontal_url || '');
          setTitleImage(s.images?.title_logo_url || '');
          setTrailerStreamUrl(s.trailer_url || '');
          setIsKidsMode(s.metadata?.is_kids_friendly || false);
          
          setIsPremium(s.is_premium || false);
          setPreviewDuration(s.preview_duration_ms ? Math.floor(s.preview_duration_ms / 1000).toString() : '60');
          setPrice(s.price_cents ? (s.price_cents / 100).toString() : '0');
          
          if (s.translations && Object.keys(s.translations).length > 0) {
            setContentTranslations(s.translations);
          } else {
            setContentTranslations({
              en: {
                title: s.title || '',
                synopsis: s.synopsis || '',
                image_url: s.media?.cover_image_url,
                video_url: s.media?.video_url,
                audio_url: s.media?.audio_url,
                subtitle_url: s.media?.subtitle_url,
                timingfile_url: s.media?.sync_data_url,
                storytxt_url: s.media?.raw_text_url
              }
            });
          }

          if (s.episodes && s.episodes.length > 0) {
            setEpisodes(s.episodes.map((ep: any) => ({
              cuid: ep.id,
              season: ep.season,
              episode_number: ep.episode_number,
              translations: (ep.translations && Object.keys(ep.translations).length > 0) ? ep.translations : {
                en: {
                  title: ep.title,
                  overview: ep.overview,
                  image_url: ep.media?.cover_image_url,
                  video_url: ep.media?.video_url,
                  audio_url: ep.media?.audio_url,
                  subtitle_url: ep.media?.subtitle_url,
                  timingfile_url: ep.media?.sync_data_url,
                  storytxt_url: ep.media?.raw_text_url
                }
              }
            })));
          }
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load story details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLists = async () => {
    try {
      const catRes = await fetch(`${API_URL}/content/categories?app_target=stories`);
      if (catRes.ok) setCategoriesList(await catRes.json());
      
      const genRes = await fetch(`${API_URL}/content/genres?app_target=stories`);
      if (genRes.ok) setGenresList(await genRes.json());
    } catch (err) {
      console.error('Failed to fetch lists', err);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newCategoryName, app_target: 'stories' })
      });
      if (res.ok) {
        setNewCategoryName('');
        fetchLists();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const addGenre = async () => {
    if (!newGenreName) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/genres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newGenreName, app_target: 'stories' })
      });
      if (res.ok) {
        setNewGenreName('');
        fetchLists();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleContentTranslationChange = (field: keyof Translation, value: string) => {
    setContentTranslations({
      ...contentTranslations,
      [activeLang]: {
        ...contentTranslations[activeLang],
        [field]: value
      }
    });
  };

  const handleAddEpisode = () => {
    setEpisodes([...episodes, { 
      season: '1', 
      episode_number: String(episodes.length + 1), 
      translations: {}, 
    }]);
  };

  const handleRemoveEpisode = (index: number) => {
    const newEpisodes = [...episodes];
    newEpisodes.splice(index, 1);
    setEpisodes(newEpisodes);
  };

  const handleEpisodeChange = (index: number, field: keyof Episode, value: string) => {
    const newEpisodes = [...episodes];
    (newEpisodes[index] as any)[field] = value;
    setEpisodes(newEpisodes);
  };

  const handleEpisodeTranslationChange = (index: number, field: keyof EpisodeTranslation, value: string) => {
    const newEpisodes = [...episodes];
    if (!newEpisodes[index].translations[activeLang]) {
      newEpisodes[index].translations[activeLang] = { title: '', overview: '' };
    }
    newEpisodes[index].translations[activeLang][field] = value;
    setEpisodes(newEpisodes);
  };

  const saveData = async (isRepost: boolean = false, translationsOverride?: any, episodesOverride?: any) => {
    setLoading(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('jwt_token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    const payload = {
      app_target: 'stories',
      type,
      is_kids_mode: isKidsMode,
      channel_id: channelId || null,
      author: channelId ? undefined : author,
      illustrator: channelId ? undefined : illustrator,
      title: (translationsOverride || contentTranslations)['ar']?.title || (translationsOverride || contentTranslations)['en']?.title || '',
      synopsis: (translationsOverride || contentTranslations)['ar']?.synopsis || (translationsOverride || contentTranslations)['en']?.synopsis || '',
      translations: translationsOverride || contentTranslations,
      category,
      director,
      writer,
      status,
      difficulty_level: difficultyLevel,
      audio,
      genres,
      cast_members: castMembers,
      release_year: releaseYear,
      duration,
      country,
      original_language: originalLanguage,
      rating,
      tmdb_id: tmdbId,
      poster_image: posterImage,
      vertical_image: verticalImage,
      title_image: titleImage,
      trailer_stream_url: trailerStreamUrl,
      is_premium: isPremium,
      preview_duration_ms: parseInt(previewDuration || '60') * 1000,
      price_cents: Math.floor(parseFloat(price || '0') * 100),
      ...(type === 'Single/Movie' ? {
        movie_details: {
          video_url: contentTranslations[activeLang]?.video_url,
          audio_url: contentTranslations[activeLang]?.audio_url,
          subtitle_url: contentTranslations[activeLang]?.subtitle_url
        }
      } : {}),
      episodes: type === 'Series' ? (episodesOverride || episodes).map((ep: any) => ({
        cuid: ep.cuid,
        season: ep.season,
        episode_number: ep.episode_number,
        title: ep.translations['ar']?.title || ep.translations['en']?.title || '',
        overview: ep.translations['ar']?.overview || ep.translations['en']?.overview || '',
        translations: Object.entries(ep.translations).reduce((acc: any, [lang, data]: [string, any]) => {
          acc[lang] = {
            title: data.title,
            overview: data.overview,
            image_url: data.image_url,
            video_url: data.video_url,
            audio_url: data.audio_url,
            subtitle_url: data.subtitle_url,
            sync_data_url: data.timingfile_url,
            raw_text_url: data.storytxt_url
          };
          return acc;
        }, {})
      })) : undefined
    };

    const uploadPendingTexts = async (targetCuid: string, authToken: string) => {
      let hasUpdates = false;
      const newTranslations = { ...contentTranslations };
      const newEpisodes = [...episodes];
  
      if (type === 'Text Book' || type === 'Single/Movie') {
        for (const lang of Object.keys(contentTranslations)) {
          const key = `story_${lang}`;
          const content = htmlContent[key];
          if (content && content.trim() !== '') {
            const blob = new Blob([content], { type: 'text/html' });
            const fileType = `storytxt_${lang}_${new Date().getTime()}`;
            const formData = new FormData();
            formData.append('file', new File([blob], `${fileType}.html`, { type: 'text/html' }));
            try {
              const res = await fetch(`${API_URL}/stories/${targetCuid}/media/${fileType}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authToken}` },
                body: formData
              });
              const upData = await res.json();
              if (res.ok) {
                const absoluteUrl = upData.url.startsWith('http') ? upData.url : `${API_URL.replace('/api/v1', '')}${upData.url}`;
                if (newTranslations[lang]) {
                  newTranslations[lang].storytxt_url = absoluteUrl;
                } else {
                  newTranslations[lang] = { title: '', synopsis: '', storytxt_url: absoluteUrl };
                }
                hasUpdates = true;
              }
            } catch(e) { console.error('Upload failed', e); }
          }
        }
      } else if (type === 'Series') {
        for (let i = 0; i < episodes.length; i++) {
          for (const lang of Object.keys(episodes[i].translations || {})) {
            const key = `ep_${i}_${lang}`;
            const content = htmlContent[key];
            if (content && content.trim() !== '') {
              const blob = new Blob([content], { type: 'text/markdown' });
              const fileType = `storytxt_${i}_${lang}_${new Date().getTime()}`;
              const formData = new FormData();
              formData.append('file', new File([blob], `${fileType}.md`, { type: 'text/markdown' }));
              try {
                const res = await fetch(`${API_URL}/stories/${targetCuid}/media/${fileType}`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${authToken}` },
                  body: formData
                });
                const upData = await res.json();
                if (res.ok) {
                  const absoluteUrl = upData.url.startsWith('http') ? upData.url : `${API_URL.replace('/api/v1', '')}${upData.url}`;
                  newEpisodes[i].translations[lang].storytxt_url = absoluteUrl;
                  hasUpdates = true;
                }
              } catch(e) { console.error('Upload failed', e); }
            }
          }
        }
      }
      return hasUpdates ? { translations: newTranslations, episodes: newEpisodes } : null;
    };

    try {
      const isEdit = !!cuid;
      const url = isEdit ? `${API_URL}/content/story/${cuid}${isRepost ? '?repost=true' : ''}` : `${API_URL}/content/story`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save story');
      }

      const savedCuid = data.cuid || cuid;

      if (savedCuid) {
        const textUpdates = await uploadPendingTexts(savedCuid, token);
        if (textUpdates) {
           setContentTranslations(textUpdates.translations);
           if (type === 'Series') setEpisodes(textUpdates.episodes);
           
           payload.translations = textUpdates.translations;
           if (type === 'Series') {
             payload.episodes = textUpdates.episodes.map((ep: any) => ({
                cuid: ep.cuid,
                season: ep.season,
                episode_number: ep.episode_number,
                title: ep.translations['ar']?.title || ep.translations['en']?.title || '',
                overview: ep.translations['ar']?.overview || ep.translations['en']?.overview || '',
                translations: Object.entries(ep.translations).reduce((acc: any, [lang, epData]: [string, any]) => {
                  acc[lang] = {
                    title: epData.title,
                    overview: epData.overview,
                    image_url: epData.image_url,
                    video_url: epData.video_url,
                    audio_url: epData.audio_url,
                    subtitle_url: epData.subtitle_url,
                    sync_data_url: epData.timingfile_url,
                    raw_text_url: epData.storytxt_url
                  };
                  return acc;
                }, {})
             }));
           }

           await fetch(`${API_URL}/content/story/${savedCuid}`, {
             method: 'PUT',
             headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${token}`
             },
             body: JSON.stringify(payload)
           });
        }
      }

      setSuccess(`Story ${isEdit ? 'updated' : 'created'} successfully!`);
      if (!isEdit) {
        setTimeout(() => navigate(`/vod/${savedCuid}`), 1500);
      } else {
        setTimeout(() => navigate('/vod-stories'), 1500);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRewriteSynopsis = async () => {
    const currentSynopsis = contentTranslations[activeLang]?.synopsis;
    if (!currentSynopsis) return;
    setIsRewritingSynopsis(true);
    try {
      const targetLanguageLabel = LANGUAGES.find(l => l.code === activeLang)?.label || 'English';
      const rewritten = await autoRewriteSynopsis(currentSynopsis, targetLanguageLabel);
      if (rewritten) {
        handleContentTranslationChange('synopsis', rewritten);
      }
    } catch (e: any) {
      alert("Failed to rewrite synopsis: " + e.message);
    } finally {
      setIsRewritingSynopsis(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveData(false);
  };

  const handleRepost = () => {
    saveData(true);
  };

  const handleAutoTranslate = async (targetLangCode?: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const sourceLangCode = getSourceLangCode();
      const currentContent = contentTranslations[sourceLangCode]?.title ? contentTranslations[sourceLangCode] : contentTranslations[activeLang];
      const actualSourceLang = contentTranslations[sourceLangCode]?.title ? sourceLangCode : activeLang;
      
      if (currentContent && currentContent.title) {
        const newTranslations = { ...contentTranslations };
        const langsToTranslate = targetLangCode ? LANGUAGES.filter(l => l.code === targetLangCode) : LANGUAGES;

        for (const lang of langsToTranslate) {
          if (lang.code === actualSourceLang) continue;
          
          let translatedObj: any = {};
          try {
            const objToTranslate = {
              title: currentContent.title || '',
              synopsis: currentContent.synopsis || ''
            };
            translatedObj = await autoTranslateObject(objToTranslate, lang.label);
          } catch (e: any) {
             console.error(`Translation failed for ${lang.code}:`, e);
          }

          const locUrl = (url?: string) => url ? url.replace(/_([a-z]{2,3})\.(\w+)$/, `.${'$2'}`).replace(/\.(\w+)$/, `_${lang.code}.$1`) : '';

          newTranslations[lang.code] = {
            ...newTranslations[lang.code],
            title: translatedObj.title || newTranslations[lang.code]?.title || '',
            synopsis: translatedObj.synopsis || newTranslations[lang.code]?.synopsis || '',
            video_url: locUrl(currentContent.video_url),
            audio_url: locUrl(currentContent.audio_url),
            subtitle_url: locUrl(currentContent.subtitle_url),
            timingfile_url: locUrl(currentContent.timingfile_url),
            storytxt_url: locUrl(currentContent.storytxt_url),
          };
        }
        setContentTranslations(newTranslations);
      }

      if (type === 'Series' && episodes.length > 0) {
        const newEpisodes = [...episodes];
        for (let i = 0; i < newEpisodes.length; i++) {
          const ep = newEpisodes[i];
          const currentEp = ep.translations[sourceLangCode]?.title ? ep.translations[sourceLangCode] : ep.translations[activeLang];
          const actualSourceLangEp = ep.translations[sourceLangCode]?.title ? sourceLangCode : activeLang;
          if (!currentEp || !currentEp.title) continue;
          
          const langsToTranslate = targetLangCode ? LANGUAGES.filter(l => l.code === targetLangCode) : LANGUAGES;

          for (const lang of langsToTranslate) {
            if (lang.code === actualSourceLangEp) continue;
            
            let translatedObj: any = {};
            try {
              const objToTranslate = {
                title: currentEp.title || '',
                overview: currentEp.overview || ''
              };
              translatedObj = await autoTranslateObject(objToTranslate, lang.label);
            } catch (e: any) {
               console.error(`Translation failed for episode in ${lang.code}:`, e);
            }

            const locUrl = (url?: string) => url ? url.replace(/_([a-z]{2,3})\.(\w+)$/, `.${'$2'}`).replace(/\.(\w+)$/, `_${lang.code}.$1`) : '';

            if (!ep.translations[lang.code]) ep.translations[lang.code] = { title: '', overview: '' };
            
            ep.translations[lang.code] = {
              ...ep.translations[lang.code],
              title: translatedObj.title || ep.translations[lang.code].title || '',
              overview: translatedObj.overview || ep.translations[lang.code].overview || '',
              video_url: locUrl(currentEp.video_url),
              audio_url: locUrl(currentEp.audio_url),
              subtitle_url: locUrl(currentEp.subtitle_url),
              image_url: locUrl(currentEp.image_url),
              timingfile_url: locUrl(currentEp.timingfile_url),
              storytxt_url: locUrl(currentEp.storytxt_url),
            };
          }
        }
        setEpisodes(newEpisodes);
      }
      
      setSuccess('Translation completed successfully.');
    } catch (err: any) {
      setError(err.message || 'Auto-translate failed. Please check your API keys in Settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoTag = async () => {
    const syn = contentTranslations['ar']?.synopsis || contentTranslations['en']?.synopsis;
    if (!syn) {
      setError("Please enter a synopsis (in Arabic or English) first to auto-tag.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const tags = await autoTagStory(syn);
      if (tags.category) {
        const catExists = categoriesList.find(c => c.name.toLowerCase() === tags.category.toLowerCase());
        if (catExists) {
          setCategory(catExists.name);
        } else {
          setNewCategoryName(tags.category);
        }
      }
      if (tags.genres && tags.genres.length > 0) {
        const genreExists = genresList.find(g => KNOWN_GENRES[g.name] && (KNOWN_GENRES[g.name].ar === tags.genres[0] || KNOWN_GENRES[g.name].en === tags.genres[0] || g.name === tags.genres[0]));
        if (genreExists) {
          setGenres(genreExists.name);
        } else {
          setNewGenreName(tags.genres[0]);
        }
      }
      setSuccess("Auto-tagging complete! Please review the suggested Category and Genre.");
    } catch (e) {
      console.error(e);
      setError("Auto-tagging failed. Check your API keys.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveText = async (lang: string, isEpisode: boolean = false, epIndex: number = -1) => {
    if (!cuid) {
      setError('Please create the story first before saving the text.');
      return;
    }
    const key = isEpisode ? `ep_${epIndex}_${lang}` : `story_${lang}`;
    const content = htmlContent[key];
    if (!content) {
      if (isEpisode) {
        handleEpisodeTranslationChange(epIndex, 'storytxt_url', '');
      } else {
        handleContentTranslationChange('storytxt_url', '');
      }
      setIsHtmlEditorOpen(prev => ({ ...prev, [key]: false }));
      setSuccess('Story text removed successfully! Save the main form to apply to database.');
      return;
    }

    setUploadingHtml(true);
    try {
      const token = localStorage.getItem('jwt_token');
      const blob = new Blob([content], { type: 'text/markdown' });
      const timestamp = new Date().getTime();
      const fileType = isEpisode ? `storytxt_${epIndex}_${lang}_${timestamp}` : `storytxt_${lang}_${timestamp}`;
      const file = new File([blob], `${fileType}.md`, { type: 'text/markdown' });
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/stories/${cuid}/media/${fileType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload Markdown file');
      
      const absoluteUrl = `${API_URL}/stories/${cuid}/media/${fileType}`;
        
      if (isEpisode) {
        handleEpisodeTranslationChange(epIndex, 'storytxt_url', absoluteUrl);
        const updatedEpisodes = [...episodes];
        updatedEpisodes[epIndex].translations[lang] = {
          ...updatedEpisodes[epIndex].translations[lang],
          storytxt_url: absoluteUrl
        };
        await saveData(false, undefined, updatedEpisodes);
      } else {
        handleContentTranslationChange('storytxt_url', absoluteUrl);
        const updatedTranslations = {
          ...contentTranslations,
          [lang]: {
            ...contentTranslations[lang],
            storytxt_url: absoluteUrl
          }
        };
        await saveData(false, updatedTranslations);
      }
      setSuccess('Story text saved successfully!');
      setIsHtmlEditorOpen(prev => ({ ...prev, [key]: false }));
    } catch (e: any) {
      setError(e.message || 'Error uploading story text');
    } finally {
      setUploadingHtml(false);
    }
  };

  const handleInsertImage = (url: string, key: string) => {
    setHtmlContent(prev => ({
      ...prev,
      [key]: (prev[key] || '') + `\n\n![Image](${url})\n\n`
    }));
  };

  const handleClearHtml = (lang: string, isEpisode: boolean = false, epIndex: number = -1) => {
    const key = isEpisode ? `ep_${epIndex}_${lang}` : `story_${lang}`;
    setHtmlContent(prev => ({ ...prev, [key]: '' }));
    
    if (isEpisode) {
      handleEpisodeTranslationChange(epIndex, 'storytxt_url', '');
    } else {
      handleContentTranslationChange('storytxt_url', '');
    }
    
    setIsHtmlEditorOpen(prev => ({ ...prev, [key]: false }));
    setSuccess('Story text removed successfully!');
  };

  const fetchHtmlContent = async (url: string, key: string) => {
    if (!url) return;
    try {
      const res = await fetch(url);
      if (res.ok) {
        const text = await res.text();
        setHtmlContent(prev => ({ ...prev, [key]: text }));
      }
    } catch (e) {
      console.error('Failed to fetch HTML', e);
    }
  };

  const toggleHtmlEditor = (lang: string, url?: string, isEpisode: boolean = false, epIndex: number = -1) => {
    const key = isEpisode ? `ep_${epIndex}_${lang}` : `story_${lang}`;
    const isEpub = url && url.toLowerCase().endsWith('.epub');
    if (isEpub) {
      alert("EPUB files cannot be edited directly in the text editor. Please upload a new text file or clear the current EPUB to write a new story.");
      return;
    }
    if (!isHtmlEditorOpen[key] && url && !htmlContent[key]) {
      fetchHtmlContent(url, key);
    }
    setIsHtmlEditorOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const completenessProgress = () => {
    let score = 0;
    const maxScore = 5;
    if (contentTranslations[activeLang]?.title) score++;
    if (contentTranslations[activeLang]?.synopsis) score++;
    if (verticalImage || posterImage || titleImage) score++;
    if (contentTranslations[activeLang]?.video_url || contentTranslations[activeLang]?.audio_url) score++;
    if (category) score++;
    return Math.round((score / maxScore) * 100);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto font-arabic">
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-slate-200 shadow-sm sticky top-0 z-10 font-arabic mb-6">
        <h1 className="text-xl font-bold text-slate-800">
          {cuid ? 'Edit Story / VOD' : 'Add New Story / VOD'}
        </h1>
        <div className="flex items-center gap-3">
          {/* Account Switcher Dropdown */}
          <div className="relative" ref={accountMenuRef}>
            <button
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              type="button"
              className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors border border-slate-200 font-medium text-sm shadow-sm"
              title="Switch Account / Logout"
            >
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Account</span>
              <Key className="w-3.5 h-3.5" />
            </button>
            {showAccountMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 animate-in fade-in-95 duration-150">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-xs font-semibold text-slate-500">Current Session</p>
                </div>
                <button
                  onClick={handleSwitchAccount}
                  type="button"
                  className="w-full text-right px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 text-sm"
                >
                  <User className="w-4 h-4" />
                  Switch Account
                </button>
                <button
                  onClick={handleLogout}
                  type="button"
                  className="w-full text-right px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>

          {cuid && (
            <button
              onClick={handleRepost}
              disabled={loading}
              className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors font-medium flex items-center gap-2"
              title="Save and update published date to now (Repost to front page)"
            >
              <RefreshCw className="w-4 h-4" />
              Republish
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors font-medium flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {cuid ? 'Save Changes' : 'Create Story'}
          </button>
        </div>
      </div>

      {/* Completeness Progress Bar */}
      <div className="mb-6 bg-white p-4 rounded-lg border border-slate-200 shadow-sm flex items-center gap-4">
        <div className="flex-1">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-slate-700">Story Completeness</span>
            <span className="text-sm font-medium text-slate-700">{completenessProgress()}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2.5">
            <div className={`h-2.5 rounded-full ${completenessProgress() === 100 ? 'bg-emerald-500' : 'bg-indigo-600'}`} style={{ width: `${completenessProgress()}%` }}></div>
          </div>
        </div>
        {completenessProgress() === 100 && (
          <div className="flex items-center gap-1 text-emerald-600 text-sm font-bold">
            <CheckCircle className="w-5 h-5" /> Ready!
          </div>
        )}
      </div>

      {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">{error}</div>}
      {success && <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md">{success}</div>}

      <div className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-4">
        <div className="flex gap-2 overflow-x-auto pb-2 items-center">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setActiveLang(lang.code)}
              type="button"
              className={`px-5 py-2 text-sm font-medium flex items-center gap-2 whitespace-nowrap rounded-full transition-all border ${activeLang === lang.code ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              <Globe className="w-4 h-4" />
              {lang.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info Section */}
        <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <h2 className="text-lg font-medium text-slate-900 flex items-center gap-2">
              <Type className="w-5 h-5 text-slate-400" />
              Information ({LANGUAGES.find(l => l.code === activeLang)?.label})
            </h2>
            <div className="flex gap-2">
              {activeLang !== 'ar' && (
                <button
                  type="button"
                  onClick={() => handleAutoTranslate(activeLang)}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-md hover:bg-indigo-100 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Wand2 className="w-4 h-4" />
                  Translate this to {LANGUAGES.find(l => l.code === activeLang)?.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleAutoTranslate()}
                disabled={loading}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Wand2 className="w-4 h-4" />
                Translate to All
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                required={activeLang === 'en'}
                value={contentTranslations[activeLang]?.title || ''}
                onChange={(e) => handleContentTranslationChange('title', e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2.5 focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
                placeholder="Story title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Content Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'Single/Movie' | 'Series' | 'Text Book')}
                className="w-full rounded-md border border-slate-300 p-2.5 focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none bg-white"
              >
                <option value="Single/Movie">قصة كاملة (Full Story)</option>
                <option value="Series">قصة بأجزاء (Story with parts)</option>
                <option value="Text Book">Text Book (قصة ورواية نصية)</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-700">Synopsis</label>
                <button
                  type="button"
                  onClick={handleRewriteSynopsis}
                  disabled={isRewritingSynopsis || !contentTranslations[activeLang]?.synopsis}
                  className="text-xs font-medium text-indigo-600 flex items-center gap-1 hover:text-indigo-800 disabled:opacity-50"
                  title="Rewrite with AI"
                >
                  {isRewritingSynopsis ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenTool size={12} />} 
                  AI Rewrite
                </button>
              </div>
              <div className="bg-white rounded-md font-arabic font-medium pb-12">
                <JoditEditor
                  value={contentTranslations[activeLang]?.synopsis || ''}
                  onBlur={(val) => handleContentTranslationChange('synopsis', val)}
                  config={{
                    readonly: false,
                    placeholder: 'Story synopsis / description...',
                    height: 250,
                    direction: 'rtl',
                    language: 'ar',
                    askBeforePasteHTML: false,
                    askBeforePasteFromWord: false,
                    defaultActionOnPaste: 'insert_as_html',
                  }}
                />
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-slate-700">Category</label>
              <div className="flex gap-2">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 bg-white"
                >
                  <option value="">Select Category...</option>
                  {categoriesList.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => { setListModalType('categories'); setListModalOpen(true); }} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-md text-sm border border-slate-200 hover:bg-slate-200" title="Manage Categories">
                  <SettingsIcon size={16}/>
                </button>
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" placeholder="New Category" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2 text-sm"
                />
                <button type="button" onClick={addCategory} className="px-3 py-2 bg-slate-200 rounded-md text-sm" title="Add new"><Plus size={16}/></button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">Genres</label>
                <button 
                  type="button" 
                  onClick={handleAutoTag} 
                  disabled={loading}
                  className="text-xs font-medium text-indigo-600 flex items-center gap-1 hover:text-indigo-800"
                  title="Auto-fill Category & Genre from Synopsis using AI"
                >
                  <Wand2 size={12} /> AI Auto-Tag
                </button>
              </div>
              <div className="flex gap-2">
                <select
                  value={genres}
                  onChange={(e) => setGenres(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 bg-white"
                >
                  <option value="">Select Genre...</option>
                  {genresList.map(g => <option key={g.id} value={g.name}>{KNOWN_GENRES[g.name] ? (activeLang === 'ar' ? KNOWN_GENRES[g.name].ar : KNOWN_GENRES[g.name].en) : g.name}</option>)}
                </select>
                <button type="button" onClick={() => { setListModalType('genres'); setListModalOpen(true); }} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-md text-sm border border-slate-200 hover:bg-slate-200" title="Manage Genres">
                  <SettingsIcon size={16}/>
                </button>
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" placeholder="New Genre" value={newGenreName} onChange={e => setNewGenreName(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2 text-sm"
                />
                <button type="button" onClick={addGenre} className="px-3 py-2 bg-slate-200 rounded-md text-sm" title="Add new"><Plus size={16}/></button>
              </div>
            </div>

            {/* Grouped Target Settings */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg border border-slate-100 mt-4">
              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-slate-700">Premium Story</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    id="premiumToggle"
                    checked={isPremium}
                    onChange={(e) => setIsPremium(e.target.checked)}
                    className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                  />
                  <label htmlFor="premiumToggle" className="text-sm text-slate-700 cursor-pointer">Requires Purchase/Subscription</label>
                </div>
                {isPremium && (
                  <div className="mt-2 pl-7">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Free Preview (Seconds)</label>
                    <input 
                      type="number" 
                      value={previewDuration} 
                      onChange={(e) => setPreviewDuration(e.target.value)} 
                      placeholder="60"
                      className="w-full rounded-md border border-slate-300 p-2 text-sm" 
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="block text-sm font-medium text-slate-700">Kids Mode</label>
                <div className="flex items-center gap-2 mt-1">
                  <input 
                    type="checkbox" 
                    id="kidsModeToggle" 
                    checked={isKidsMode} 
                    onChange={(e) => setIsKidsMode(e.target.checked)} 
                    className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                  />
                  <label htmlFor="kidsModeToggle" className="text-sm text-slate-700 cursor-pointer">Enable Kids Mode (for children's content)</label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Director</label>
              <input type="text" value={director} onChange={(e) => setDirector(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Writer</label>
              <input type="text" value={writer} onChange={(e) => setWriter(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Channel / Narrator</label>
              <select value={channelId} onChange={(e) => {
                const id = e.target.value;
                setChannelId(id);
                if (id) {
                  const channel = channels.find(c => c.id?.toString() === id || c.cuid === id);
                  if (channel) {
                    if (channel.difficulty_level) setDifficultyLevel(channel.difficulty_level);
                    if (channel.publish_status) setStatus(channel.publish_status);
                    if (channel.audio_language) setAudio(channel.audio_language);
                    if (channel.cast_members) setCastMembers(channel.cast_members);
                    if (channel.release_year) setReleaseYear(channel.release_year.toString());
                    if (channel.country) setCountry(channel.country);
                    if (channel.original_language) setOriginalLanguage(channel.original_language);
                    if (channel.rating) setRating(channel.rating);
                    if (channel.director) setDirector(channel.director);
                    if (channel.writer) setWriter(channel.writer);
                  }
                }
              }} className="w-full rounded-md border border-slate-300 p-2.5 bg-white">
                <option value="">-- Select Channel --</option>
                {channels.map(ch => (
                  <option key={ch.cuid} value={ch.id || ch.cuid}>{ch.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Select a channel to display its name and logo for this story.</p>
            </div>
            
            {!channelId && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Legacy Author Name (Fallback)</label>
                  <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Legacy Logo URL (Fallback)</label>
                  <input type="url" value={illustrator} onChange={(e) => setIllustrator(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" dir="ltr" />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty Level</label>
              <select value={difficultyLevel} onChange={(e) => setDifficultyLevel(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5 bg-white">
                <option value="">None</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Publish Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5 bg-white">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Audio Language</label>
              <input type="text" value={audio} onChange={(e) => setAudio(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cast Members</label>
              <input type="text" value={castMembers} onChange={(e) => setCastMembers(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Release Year</label>
              <input type="text" value={releaseYear} onChange={(e) => setReleaseYear(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
              <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2.5 bg-white focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
              >
                <option value="">Select Country...</option>
                {COUNTRIES_LIST.map(c => <option key={c.value} value={c.value}>{activeLang === 'ar' ? c.label.ar : c.label.en}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Original Language</label>
              <select
                value={originalLanguage}
                onChange={(e) => setOriginalLanguage(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2.5 bg-white focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
              >
                <option value="">Select Language...</option>
                {LANGUAGES_LIST.map(l => <option key={l.value} value={l.value}>{activeLang === 'ar' ? l.label.ar : l.label.en}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rating</label>
              <input type="text" value={rating} onChange={(e) => setRating(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">TMDB ID</label>
              <input type="text" value={tmdbId} onChange={(e) => setTmdbId(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
          </div>
        </section>

        {/* Global Images Section */}
        <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <h2 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-slate-400" />
            Story Images & Main Trailer
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-slate-700">Vertical Image URL</label>
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => openArchiveExplorer(null, 'verticalImage')}
                    className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                    title="Browse Archive.org"
                  >
                    <span>🏛️</span> Archive
                  </button>
                  {cuid && <DriveUploader cuid={cuid} fileType="cover_image" accept="image/*" onUploadSuccess={(url) => setVerticalImage(url)} />}
                </div>
              </div>
              <input type="url" value={verticalImage} onChange={(e) => setVerticalImage(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-slate-700">Poster Image URL</label>
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => openArchiveExplorer(null, 'posterImage')}
                    className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                    title="Browse Archive.org"
                  >
                    <span>🏛️</span> Archive
                  </button>
                  {cuid && <DriveUploader cuid={cuid} fileType="cover_image" accept="image/*" onUploadSuccess={(url) => setPosterImage(url)} />}
                </div>
              </div>
              <input type="url" value={posterImage} onChange={(e) => setPosterImage(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-slate-700">Title Image URL</label>
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => openArchiveExplorer(null, 'titleImage')}
                    className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                    title="Browse Archive.org"
                  >
                    <span>🏛️</span> Archive
                  </button>
                  {cuid && <DriveUploader cuid={cuid} fileType="cover_image" accept="image/*" onUploadSuccess={(url) => setTitleImage(url)} />}
                </div>
              </div>
              <input type="url" value={titleImage} onChange={(e) => setTitleImage(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-slate-700">Trailer Stream URL</label>
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => openArchiveExplorer(null, 'trailerStreamUrl')}
                    className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                    title="Browse Archive.org"
                  >
                    <span>🏛️</span> Archive
                  </button>
                  {cuid && <DriveUploader cuid={cuid} fileType="video" accept="video/mp4" onUploadSuccess={(url) => setTrailerStreamUrl(url)} />}
                </div>
              </div>
              <input type="url" value={trailerStreamUrl} onChange={(e) => setTrailerStreamUrl(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
          </div>
        </section>

        {/* Full Story Media Section */}
        {type !== 'Series' && (
          <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <h2 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Film className="w-5 h-5 text-slate-400" />
              Full Story Media Links ({LANGUAGES.find(l => l.code === activeLang)?.label})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-slate-700">Video URL (.mp4)</label>
                  <div className="flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => openArchiveExplorer(null, 'video_url')}
                      className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                      title="Browse Archive.org"
                    >
                      <span>🏛️</span> Archive
                    </button>
                    {cuid && <DriveUploader cuid={cuid} fileType="video" accept="video/mp4" onUploadSuccess={(url) => handleContentTranslationChange('video_url', url)} />}
                  </div>
                </div>
                <input type="url" value={contentTranslations[activeLang]?.video_url || ''} onChange={(e) => handleContentTranslationChange('video_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
                {((contentTranslations[activeLang]?.video_url || '').includes('youtube.com') || (contentTranslations[activeLang]?.video_url || '').includes('youtu.be')) && (
                  <div className="flex gap-4 mt-2 bg-slate-50 p-3 rounded-md border border-slate-200">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-700 mb-1">Start Time (sec)</label>
                      <input type="number" placeholder="0" value={getUrlParam(contentTranslations[activeLang]?.video_url || '', 'start') || getUrlParam(contentTranslations[activeLang]?.video_url || '', 't')} onChange={(e) => {
                        let newUrl = updateUrlParam(contentTranslations[activeLang]?.video_url || '', 'start', e.target.value);
                        newUrl = updateUrlParam(newUrl, 't', '');
                        handleContentTranslationChange('video_url', newUrl);
                      }} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-700 mb-1">End Time (sec)</label>
                      <input type="number" placeholder="0" value={getUrlParam(contentTranslations[activeLang]?.video_url || '', 'end')} onChange={(e) => handleContentTranslationChange('video_url', updateUrlParam(contentTranslations[activeLang]?.video_url || '', 'end', e.target.value))} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-slate-700">Audio URL (.mp3)</label>
                  <div className="flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => openArchiveExplorer(null, 'audio_url')}
                      className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                      title="Browse Archive.org"
                    >
                      <span>🏛️</span> Archive
                    </button>
                    {cuid && <DriveUploader cuid={cuid} fileType="audio" accept="audio/mp3,audio/mpeg" onUploadSuccess={(url) => handleContentTranslationChange('audio_url', url)} />}
                  </div>
                </div>
                <input type="url" value={contentTranslations[activeLang]?.audio_url || ''} onChange={(e) => handleContentTranslationChange('audio_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
                {((contentTranslations[activeLang]?.audio_url || '').includes('youtube.com') || (contentTranslations[activeLang]?.audio_url || '').includes('youtu.be')) && (
                  <div className="flex gap-4 mt-2 bg-slate-50 p-3 rounded-md border border-slate-200">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-700 mb-1">Start Time (sec)</label>
                      <input type="number" placeholder="0" value={getUrlParam(contentTranslations[activeLang]?.audio_url || '', 'start') || getUrlParam(contentTranslations[activeLang]?.audio_url || '', 't')} onChange={(e) => {
                        let newUrl = updateUrlParam(contentTranslations[activeLang]?.audio_url || '', 'start', e.target.value);
                        newUrl = updateUrlParam(newUrl, 't', '');
                        handleContentTranslationChange('audio_url', newUrl);
                      }} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-700 mb-1">End Time (sec)</label>
                      <input type="number" placeholder="0" value={getUrlParam(contentTranslations[activeLang]?.audio_url || '', 'end')} onChange={(e) => handleContentTranslationChange('audio_url', updateUrlParam(contentTranslations[activeLang]?.audio_url || '', 'end', e.target.value))} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-slate-700">Story HTML/Text Link (Optional)</label>
                  <button
                    type="button"
                    onClick={() => openArchiveExplorer(null, 'storytxt_url')}
                    className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                    title="Browse Archive.org"
                  >
                    <span>🏛️</span> Archive
                  </button>
                </div>
                <input type="url" placeholder="https://example.com/story.html" value={contentTranslations[activeLang]?.storytxt_url || ''} onChange={(e) => handleContentTranslationChange('storytxt_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-slate-700">Subtitle URL (.srt)</label>
                  <div className="flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => openArchiveExplorer(null, 'subtitle_url')}
                      className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                      title="Browse Archive.org"
                    >
                      <span>🏛️</span> Archive
                    </button>
                    {cuid && activeLang !== getSourceLangCode() && (
                      <button
                        type="button"
                        onClick={handleTranslateSubtitle}
                        disabled={isTranslatingSrt}
                        className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium hover:bg-indigo-100 flex items-center gap-1 disabled:opacity-50"
                        title="Translate using original subtitle"
                      >
                        {isTranslatingSrt ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                        Translate
                      </button>
                    )}
                    {cuid && <DriveUploader cuid={cuid} fileType={`subtitle_${activeLang}`} accept=".srt,.vtt" onUploadSuccess={(url) => handleContentTranslationChange('subtitle_url', url)} />}
                  </div>
                </div>
                <input type="url" value={contentTranslations[activeLang]?.subtitle_url || ''} onChange={(e) => handleContentTranslationChange('subtitle_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-slate-700">Timing File URL (.json)</label>
                  <div className="flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => openArchiveExplorer(null, 'timingfile_url')}
                      className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                      title="Browse Archive.org"
                    >
                      <span>🏛️</span> Archive
                    </button>
                    {cuid && <DriveUploader cuid={cuid} fileType="metadata" accept="application/json" onUploadSuccess={(url) => handleContentTranslationChange('timingfile_url', url)} />}
                  </div>
                </div>
                <input type="url" value={contentTranslations[activeLang]?.timingfile_url || ''} onChange={(e) => handleContentTranslationChange('timingfile_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div className="md:col-span-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-slate-700">Story Text URL (.md, .txt)</label>
                  <div className="flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => openArchiveExplorer(null, 'storytxt_url')}
                      className="px-2 py-1 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                      title="Browse Archive.org"
                    >
                      <span>🏛️</span> Archive
                    </button>
                    <button type="button" onClick={() => toggleHtmlEditor(activeLang, contentTranslations[activeLang]?.storytxt_url)} className="text-xs flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md font-medium">
                      <PenTool size={14} /> {isHtmlEditorOpen[`story_${activeLang}`] ? 'Close Editor' : 'Write Story'}
                    </button>
                    {cuid && <DriveUploader cuid={cuid} fileType={`storytxt_${activeLang}`} accept=".md,.html,.txt,.epub" onUploadSuccess={(url) => handleContentTranslationChange('storytxt_url', url)} />}
                  </div>
                </div>
                <input type="url" value={contentTranslations[activeLang]?.storytxt_url || ''} onChange={(e) => handleContentTranslationChange('storytxt_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5 mb-2" />
                
                {isHtmlEditorOpen[`story_${activeLang}`] && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden mt-2 bg-white">
                    <div className="bg-slate-50 p-2 border-b border-slate-200 flex justify-between items-center" dir="rtl">
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-slate-700">كتابة قصة: {LANGUAGES.find(l => l.code === activeLang)?.label}</span>
                        {cuid && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">إدراج صورة:</span>
                            <DriveUploader 
                              cuid={cuid} 
                              fileType={`storyimg_${activeLang}`} 
                              accept="image/*" 
                              onUploadSuccess={(url) => handleInsertImage(url, `story_${activeLang}`)} 
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleClearHtml(activeLang)} className="bg-red-50 text-red-600 px-4 py-1.5 rounded text-sm font-medium flex items-center gap-2 hover:bg-red-100 border border-red-200">
                          <Trash2 size={14} /> مسح وإزالة
                        </button>
                        <button type="button" onClick={() => handleSaveText(activeLang)} disabled={uploadingHtml} className="bg-indigo-600 text-white px-4 py-1.5 rounded text-sm font-medium flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50">
                          {uploadingHtml ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} حفظ النص
                        </button>
                      </div>
                    </div>
                    <div className="pb-12 h-[600px] bg-white p-2 text-slate-900">
                      <ReactQuill
                        theme="snow"
                        value={htmlContent[`story_${activeLang}`] || ''}
                        onChange={(val) => setHtmlContent(prev => ({ ...prev, [`story_${activeLang}`]: val || '' }))}
                        className="h-[500px]"
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link', 'image'],
                            ['clean'],
                            [{ 'direction': 'rtl' }, { 'align': [] }]
                          ],
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Story with parts (Series) Section */}
        {type === 'Series' && (
          <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-slate-900 flex items-center gap-2">
                <LayoutList className="w-5 h-5 text-slate-400" />
                Parts / Episodes ({LANGUAGES.find(l => l.code === activeLang)?.label})
              </h2>
              <button
                type="button"
                onClick={handleAddEpisode}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors text-sm font-medium"
              >
                <Plus size={16} />
                Add Part
              </button>
            </div>

            <div className="space-y-6">
              {episodes.map((ep, index) => (
                <div key={index} className="p-4 border border-slate-200 rounded-lg bg-slate-50 relative">
                  <button
                    type="button"
                    onClick={() => handleRemoveEpisode(index)}
                    className="absolute top-4 right-4 text-slate-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Season</label>
                        <input
                          type="text"
                          value={ep.season}
                          onChange={(e) => handleEpisodeChange(index, 'season', e.target.value)}
                          className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Part / Episode #</label>
                        <input
                          type="text"
                          value={ep.episode_number}
                          onChange={(e) => handleEpisodeChange(index, 'episode_number', e.target.value)}
                          className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={ep.translations[activeLang]?.title || ''}
                        onChange={(e) => handleEpisodeTranslationChange(index, 'title', e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Overview</label>
                      <textarea
                        value={ep.translations[activeLang]?.overview || ''}
                        onChange={(e) => handleEpisodeTranslationChange(index, 'overview', e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        rows={2}
                        placeholder={`Overview in ${activeLang}`}
                      />
                    </div>

                    {/* Episode Media */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Video URL</label>
                        <button
                          type="button"
                          onClick={() => openArchiveExplorer(index, 'video_url')}
                          className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                          title="Browse Archive.org"
                        >
                          <span>🏛️</span> Archive
                        </button>
                      </div>
                      <input type="url" value={ep.translations[activeLang]?.video_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'video_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                      {((ep.translations[activeLang]?.video_url || '').includes('youtube.com') || (ep.translations[activeLang]?.video_url || '').includes('youtu.be')) && (
                        <div className="flex gap-4 mt-2 bg-slate-50 p-2 rounded-md border border-slate-200">
                          <div className="flex-1">
                            <label className="block text-[10px] font-medium text-slate-700 mb-1">Start Time (sec)</label>
                            <input type="number" placeholder="0" value={getUrlParam(ep.translations[activeLang]?.video_url || '', 'start') || getUrlParam(ep.translations[activeLang]?.video_url || '', 't')} onChange={(e) => {
                              let newUrl = updateUrlParam(ep.translations[activeLang]?.video_url || '', 'start', e.target.value);
                              newUrl = updateUrlParam(newUrl, 't', '');
                              handleEpisodeTranslationChange(index, 'video_url', newUrl);
                            }} className="w-full rounded-md border border-slate-300 p-1.5 text-xs" />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] font-medium text-slate-700 mb-1">End Time (sec)</label>
                            <input type="number" placeholder="0" value={getUrlParam(ep.translations[activeLang]?.video_url || '', 'end')} onChange={(e) => handleEpisodeTranslationChange(index, 'video_url', updateUrlParam(ep.translations[activeLang]?.video_url || '', 'end', e.target.value))} className="w-full rounded-md border border-slate-300 p-1.5 text-xs" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Audio URL</label>
                        <button
                          type="button"
                          onClick={() => openArchiveExplorer(index, 'audio_url')}
                          className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                          title="Browse Archive.org"
                        >
                          <span>🏛️</span> Archive
                        </button>
                      </div>
                      <input type="url" value={ep.translations[activeLang]?.audio_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'audio_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                      {((ep.translations[activeLang]?.audio_url || '').includes('youtube.com') || (ep.translations[activeLang]?.audio_url || '').includes('youtu.be')) && (
                        <div className="flex gap-4 mt-2 bg-slate-50 p-2 rounded-md border border-slate-200">
                          <div className="flex-1">
                            <label className="block text-[10px] font-medium text-slate-700 mb-1">Start Time (sec)</label>
                            <input type="number" placeholder="0" value={getUrlParam(ep.translations[activeLang]?.audio_url || '', 'start') || getUrlParam(ep.translations[activeLang]?.audio_url || '', 't')} onChange={(e) => {
                              let newUrl = updateUrlParam(ep.translations[activeLang]?.audio_url || '', 'start', e.target.value);
                              newUrl = updateUrlParam(newUrl, 't', '');
                              handleEpisodeTranslationChange(index, 'audio_url', newUrl);
                            }} className="w-full rounded-md border border-slate-300 p-1.5 text-xs" />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] font-medium text-slate-700 mb-1">End Time (sec)</label>
                            <input type="number" placeholder="0" value={getUrlParam(ep.translations[activeLang]?.audio_url || '', 'end')} onChange={(e) => handleEpisodeTranslationChange(index, 'audio_url', updateUrlParam(ep.translations[activeLang]?.audio_url || '', 'end', e.target.value))} className="w-full rounded-md border border-slate-300 p-1.5 text-xs" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Story HTML/Text Link (Optional)</label>
                        <button
                          type="button"
                          onClick={() => openArchiveExplorer(index, 'storytxt_url')}
                          className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                          title="Browse Archive.org"
                        >
                          <span>🏛️</span> Archive
                        </button>
                      </div>
                      <input type="url" placeholder="https://example.com/story.html" value={ep.translations[activeLang]?.storytxt_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'storytxt_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Subtitle URL</label>
                        <div className="flex gap-2 items-center">
                          <button
                            type="button"
                            onClick={() => openArchiveExplorer(index, 'subtitle_url')}
                            className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                            title="Browse Archive.org"
                          >
                            <span>🏛️</span> Archive
                          </button>
                          {cuid && <DriveUploader cuid={cuid} fileType={`ep_${index}_subtitle_${activeLang}`} accept=".srt,.vtt" onUploadSuccess={(url) => handleEpisodeTranslationChange(index, 'subtitle_url', url)} />}
                        </div>
                      </div>
                      <input type="url" value={ep.translations[activeLang]?.subtitle_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'subtitle_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Image URL</label>
                        <button
                          type="button"
                          onClick={() => openArchiveExplorer(index, 'image_url')}
                          className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                          title="Browse Archive.org"
                        >
                          <span>🏛️</span> Archive
                        </button>
                      </div>
                      <input type="url" value={ep.translations[activeLang]?.image_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'image_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Timing File URL</label>
                        <button
                          type="button"
                          onClick={() => openArchiveExplorer(index, 'timingfile_url')}
                          className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                          title="Browse Archive.org"
                        >
                          <span>🏛️</span> Archive
                        </button>
                      </div>
                      <input type="url" value={ep.translations[activeLang]?.timingfile_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'timingfile_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-xs font-medium text-slate-600">Story Text URL (.md)</label>
                        <div className="flex gap-2 items-center">
                          <button
                            type="button"
                            onClick={() => openArchiveExplorer(index, 'storytxt_url')}
                            className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 border border-amber-200"
                            title="Browse Archive.org"
                          >
                            <span>🏛️</span> Archive
                          </button>
                          <button type="button" onClick={() => toggleHtmlEditor(activeLang, ep.translations[activeLang]?.storytxt_url, true, index)} className="text-xs flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded font-medium">
                            <PenTool size={12} /> {isHtmlEditorOpen[`ep_${index}_${activeLang}`] ? 'Close' : 'Write'}
                          </button>
                        </div>
                      </div>
                      <input type="url" value={ep.translations[activeLang]?.storytxt_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'storytxt_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm mb-2" />
                      
                      {isHtmlEditorOpen[`ep_${index}_${activeLang}`] && (
                        <div className="border border-slate-200 rounded-lg overflow-hidden mt-2 bg-white">
                          <div className="bg-slate-50 p-2 border-b border-slate-200 flex justify-between items-center" dir="rtl">
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-medium text-slate-700">كتابة قصة (جزء {ep.episode_number})</span>
                              {cuid && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-slate-500">إدراج صورة:</span>
                                  <DriveUploader 
                                    cuid={cuid} 
                                    fileType={`ep_${index}_storyimg_${activeLang}`} 
                                    accept="image/*" 
                                    onUploadSuccess={(url) => handleInsertImage(url, `ep_${index}_${activeLang}`)} 
                                  />
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => handleClearHtml(activeLang, true, index)} className="bg-red-50 text-red-600 px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:bg-red-100 border border-red-200">
                                <Trash2 size={12} /> إزالة
                              </button>
                              <button type="button" onClick={() => handleSaveText(activeLang, true, index)} disabled={uploadingHtml} className="bg-indigo-600 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:bg-indigo-700 disabled:opacity-50">
                                {uploadingHtml ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} حفظ
                              </button>
                            </div>
                          </div>
                          <div className="pb-12 h-[400px] bg-white text-slate-900">
                              <ReactQuill
                                theme="snow"
                                value={htmlContent[`ep_${index}_${activeLang}`] || ''}
                                onChange={(val) => setHtmlContent(prev => ({ ...prev, [`ep_${index}_${activeLang}`]: val || '' }))}
                                className="h-[300px]"
                                modules={{
                                  toolbar: [
                                    [{ 'header': [1, 2, 3, false] }],
                                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                    [{'list': 'ordered'}, {'list': 'bullet'}],
                                    ['link', 'image'],
                                    ['clean'],
                                    [{ 'direction': 'rtl' }, { 'align': [] }]
                                  ],
                                }}
                              />
                            </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              ))}
              {episodes.length === 0 && (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  No parts added yet. Click "Add Part" to begin.
                </div>
              )}
            </div>
          </section>
        )}

        <div className="flex justify-end pt-4 gap-4">
          {cuid && (
            <button
              type="button"
              onClick={handleRepost}
              disabled={loading}
              className="px-6 py-3 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors font-medium flex items-center gap-2"
              title="Save and update published date to now (Repost to front page)"
            >
              <RefreshCw className="w-5 h-5" />
              Republish
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50 font-medium"
          >
            <Save size={20} />
            {loading ? 'Saving...' : 'Save Story'}
          </button>
        </div>
      </form>

      <ListManagerModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        type={listModalType}
        apiUrl={API_URL}
        token={localStorage.getItem('jwt_token')}
        onUpdate={fetchLists}
      />

      <ArchiveFileExplorer
        isOpen={isArchiveOpen}
        onClose={() => setIsArchiveOpen(false)}
        onSelect={handleArchiveSelect}
        userEmail={ARCHIVE_USER_EMAIL}
      />
    </div>
  );
}
